import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ScanSearch } from "lucide-react";
import { apiClient } from "../App";

const IDEA_MIN = 10;
const IDEA_MAX = 500;

const DashboardIdeaCheckPage = () => {
  const [ideaInput, setIdeaInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [quotaLoading, setQuotaLoading] = useState(true);
  const [quota, setQuota] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  /** Only true for daily quota exhausted — avoid matching "problem" → false "pro" upgrade hint */
  const [showQuotaUpgradeHint, setShowQuotaUpgradeHint] = useState(false);

  const loadQuota = useCallback(async () => {
    setQuotaLoading(true);
    try {
      const { data } = await apiClient.get("/ideas/analyze-quota");
      setQuota(data);
    } catch {
      setQuota(null);
    } finally {
      setQuotaLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQuota();
  }, [loadQuota]);

  const analyze = async () => {
    const idea = ideaInput.trim();
    if (idea.length < IDEA_MIN || idea.length > IDEA_MAX) {
      setShowQuotaUpgradeHint(false);
      setError(`Describe your idea in ${IDEA_MIN}–${IDEA_MAX} characters.`);
      return;
    }

    setLoading(true);
    setError("");
    setShowQuotaUpgradeHint(false);
    setResult(null);
    try {
      const { data } = await apiClient.post("/ideas/analyze", { idea });
      const score = Number(data?.score) || 0;
      const tone =
        score >= 78 ? "text-emerald-300" : score >= 60 ? "text-amber-200" : "text-zinc-300";
      const revenueLine = data?.revenue ?? data?.monetization ?? "";
      setResult({ ...data, revenue: revenueLine, tone, quota: data?.quota });
      if (data?.quota) setQuota(data.quota);
      else await loadQuota();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429 && typeof detail === "object" && detail?.code === "IDEA_DAILY_LIMIT_REACHED") {
        setShowQuotaUpgradeHint(true);
        setError(
          detail.message ||
            "Daily validation limit reached. Upgrade to Pro for up to 8 validations per sliding day window."
        );
      } else if (typeof detail === "string") {
        setShowQuotaUpgradeHint(false);
        setError(detail);
      } else if (Array.isArray(detail)) {
        setShowQuotaUpgradeHint(false);
        setError(detail.map((x) => x?.msg || "").filter(Boolean).join(" ") || "Invalid request.");
      } else {
        setShowQuotaUpgradeHint(false);
        setError("Could not run the analysis right now. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const quotaLabel =
    quota && !quotaLoading
      ? `Today: ${quota.remaining_today}/${quota.limit_per_day} validations left`
      : null;

  return (
    <div className="px-4 py-5 md:p-8 lg:p-12 max-w-3xl mx-auto">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-[#A78BFA]">
            <ScanSearch className="h-6 w-6" aria-hidden />
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-500">
              SaaS Radar
            </span>
          </div>
          <h1
            className="text-2xl md:text-3xl font-bold tracking-tight text-white"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Validate a SaaS idea
          </h1>
          <p className="mt-1 text-sm text-zinc-400">
            Free: 1 analysis per day · Pro: up to 8 per day
          </p>
        </div>
        {quotaLabel ? (
          <p className="text-sm font-medium tabular-nums text-zinc-300 whitespace-nowrap">
            {quotaLabel}
          </p>
        ) : null}
      </div>

      <div className="rounded-2xl border border-white/[0.08] bg-[#161616]/80 p-5 md:p-6 shadow-[0_20px_50px_-30px_rgba(0,0,0,0.8)]">
        <textarea
          value={ideaInput}
          onChange={(e) => setIdeaInput(e.target.value)}
          placeholder="SaaS tool for remote team collaboration…"
          maxLength={IDEA_MAX}
          rows={6}
          className="min-h-[140px] w-full resize-y rounded-xl border border-white/[0.08] bg-black/35 px-4 py-3 text-[15px] leading-relaxed text-white outline-none transition-shadow focus:border-[#7C3AED]/50 focus:ring-2 focus:ring-[#7C3AED]/25 placeholder:text-zinc-500"
          disabled={loading}
        />
        <div className="mt-2 flex justify-end text-[11px] text-zinc-600 tabular-nums">
          {ideaInput.length}/{IDEA_MAX}
        </div>

        <button
          type="button"
          onClick={analyze}
          disabled={
            loading || ideaInput.trim().length < IDEA_MIN || ideaInput.trim().length > IDEA_MAX
          }
          className="mt-4 inline-flex min-h-[3rem] w-full items-center justify-center rounded-full bg-gradient-to-r from-[#9D74F7] via-[#7C3AED] to-[#6D28D9] px-6 text-[15px] font-semibold text-white shadow-[0_12px_32px_-8px_rgba(124,58,237,0.45)] transition hover:brightness-105 hover:shadow-[0_14px_36px_-6px_rgba(124,58,237,0.5)] active:scale-[0.99] disabled:pointer-events-none disabled:opacity-40"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="h-5 w-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              Analyzing…
            </span>
          ) : (
            "Get my analysis →"
          )}
        </button>

        <p className="mt-3 text-center text-xs text-zinc-500">
          No credit card • Processed securely on our servers
        </p>

        {error ? (
          <div className="mt-5 rounded-xl border border-rose-500/25 bg-rose-500/[0.08] px-4 py-3 text-sm text-rose-100">
            <p>{error}</p>
            {showQuotaUpgradeHint ? (
              <Link
                to="/dashboard/settings/billing"
                className="mt-2 inline-block text-[#C4B5FD] underline-offset-4 hover:underline font-medium"
              >
                Upgrade to Pro →
              </Link>
            ) : null}
          </div>
        ) : null}

        {result ? (
          <div className="mt-8 rounded-xl border border-white/[0.08] bg-black/30 p-5">
            <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-zinc-500">Score</p>
                <p
                  className={`text-3xl font-semibold ${result.tone}`}
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  {result.score}
                  <span className="text-lg font-normal text-zinc-500">/100</span>
                </p>
              </div>
              <span
                className={`rounded-full border border-white/[0.1] px-3 py-1 text-xs font-semibold ${result.tone}`}
              >
                {result.verdict}
              </span>
            </div>
            <dl className="space-y-3 text-sm leading-relaxed text-zinc-400">
              <div>
                <dt className="font-medium text-zinc-200">Market</dt>
                <dd>{result.market}</dd>
              </div>
              <div>
                <dt className="font-medium text-zinc-200">Revenue</dt>
                <dd>{result.revenue}</dd>
              </div>
              {Array.isArray(result.next_steps) && result.next_steps.length ? (
                <div>
                  <dt className="font-medium text-zinc-200">Next steps</dt>
                  <dd>
                    <ol className="mt-2 list-decimal space-y-1 pl-5">
                      {result.next_steps.map((step, i) => (
                        <li key={`${step}-${i}`}>{step}</li>
                      ))}
                    </ol>
                  </dd>
                </div>
              ) : null}
              <div>
                <dt className="font-medium text-zinc-200">Competition</dt>
                <dd>{result.competition}</dd>
              </div>
              <div>
                <dt className="font-medium text-zinc-200">Risks</dt>
                <dd>{result.risks}</dd>
              </div>
            </dl>
            {result.quota ? (
              <p className="mt-5 text-xs text-zinc-500">
                Remaining today after this run: {result.quota.remaining_today} /{" "}
                {result.quota.limit_per_day}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DashboardIdeaCheckPage;
